<template>
  <div class="card-my-offered">
    <card-info
      class="card-details"
      :item="offered"
      :hide-operator="true"
      :show-year="true"
    />
    <div
      v-if="statusIsNotSold"
      class="card-actions"
    >
      <div class="status-wrapper">
        <v-chip
          class="status-chip font-weight-bold"
          :color="offered.status"
          text-color="white"
        >
          {{ offered.status_text }}
        </v-chip>
      </div>

      <div class="summary-wrapper">
        <div class="action-title">
          {{ $t('components.card_my_offers.total_value') }}
        </div>
        <div class="total-value">
          {{ formatMoney(totalValue) }}
        </div>
      </div>

      <div class="action-form">
        <div class="text-info">
          {{ $t('components.card_my_offers.unit_value') }}
        </div>
        <input
          v-model="unitValue"
          v-mask="['##', '#,##', '##,##', '###,##']"
          type="text"
          class="field-offered"
          placeholder="..."
          :disabled="!creditOfferedActive"
        >
      </div>
      <div
        class="button-actions"
      >
        <rectoplus-button
          class="save-button"
          :text="$t('components.card_my_offers.buttons.save')"
          :disabled="validateBeforeSave || !creditOfferedActive"
          @click="emitUpdate"
        />
        <rectoplus-button
          v-if="offered.available_to_delete"
          class="delete-button"
          :text="$t('components.card_my_offers.buttons.delete')"
          color="red"
          :disabled="!creditOfferedActive"
          @click="emitDelete"
        />
      </div>
    </div>
    <div
      v-else
      class="card-info-sold"
    >
      <div class="status-wrapper">
        <v-chip
          class="status-chip font-weight-bold"
          :color="offered.status"
          text-color="white"
        >
          {{ offered.status_text }}
        </v-chip>
      </div>
      <div class="summary-wrapper">
        <div class="action-title">
          {{ $t('components.card_my_offers.total_value') }}
        </div>
        <div class="total-value">
          {{ formatMoney(totalValue) }}
        </div>
      </div>

      <div class="action-form">
        <div class="text-info">
          {{ $t('components.card_my_offers.unit_value') }}
        </div>
        <input
          v-model="unitValue"
          v-mask="['##', '#,##', '##,##', '###,##']"
          type="text"
          class="field-offered"
          placeholder="..."
          :disabled="!creditOfferedActive"
        >
      </div>
    </div>
  </div>
</template>
<script>
import { CreditOfferedStatus } from '@/enums/CreditOfferedStatus';
import formatMoney from '@/mixins/formatMoney';
import formatMask from '@/mixins/formatMask';
import RectoplusButton from '@/components/buttons/RectoplusButton.vue';
import CardInfo from '@/components/cards/offered/CardInfo.vue';

export default {
  name: 'CardMyOffered',
  components: { RectoplusButton, CardInfo },
  mixins: [formatMoney, formatMask],
  props: {
    offered: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      unitValue: '',
    };
  },
  computed: {
    validateBeforeSave() {
      return this.validatePresence || this.validateValue || !this.isEdit;
    },

    validatePresence() {
      return !this.unitValue;
    },

    validateValue() {
      return this.unitValue <= 0;
    },

    formatUnitValue() {
      return this.decimalMaskToNumber(this.unitValue);
    },

    isEdit() {
      return Number(this.totalValue) !== Number(this.offered.total_value);
    },

    creditOfferedActive() {
      return this.offered.status === CreditOfferedStatus.ACTIVE;
    },

    totalValue() {
      if (this.formatUnitValue) return Number(this.formatUnitValue * this.offered.credits_quantity).toFixed(2);
      return Number(this.offered.unit_value * this.offered.credits_quantity).toFixed(2);
    },

    statusIsNotSold() {
      return this.offered.status !== CreditOfferedStatus.SOLD;
    },

    hasRelationOffers() {
      return this.offered.related_offers.length > 0;
    },
  },
  watch: {
    offered(value) {
      this.unitValue = this.formatMoney(Number(value.unit_value));
    },
  },
  mounted() {
    this.unitValue = this.formatMoney(Number(this.offered.unit_value));
  },
  methods: {
    emitDelete() {
      if (this.hasRelationOffers) {
        return this.$emit('openConfirmDialog', this.offered.related_offers, this.offered.id);
      }

      return this.$emit('delete', this.offered.id);
    },

    emitUpdate() {
      this.$emit('update', this.offered.id, this.formatUnitValue);
    },
  },
};
</script>
<style lang="scss" scoped>
.card-my-offered {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 16px;
  background: $card_offer;
  border-radius: 10px;
  padding: 16px 20px;
  box-shadow: $box_shadow_my_offereds;
  margin-bottom: 20px;

  .card-details {
    flex: 1;
    min-width: 0;
  }

  .card-actions,
  .card-info-sold {
    display: flex;
    flex-direction: column;
    width: 20em;
    min-width: 20em;
    border-left: 1px solid $border_color_divider;
    padding-left: 24px;

    .action-title {
      font-size: 0.95em;
      color: $color_gray;
      margin-bottom: 6px;
    }

    .total-value {
      font-size: 1.75em;
      color: $color_dark;
      font-weight: bold;
    }

    .status-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 12px;
    }

    .status-chip {
      margin: 0;
    }

    .summary-wrapper {
      padding-bottom: 12px;
      margin-bottom: 12px;
      border-bottom: 1px solid $border_color_divider;
    }

    .button-actions {
      margin-top: auto;

      .save-button,
      .delete-button {
        width: 100%;
        height: 42px !important;
        min-height: 42px !important;

        ::v-deep .v-btn__content {
          font-size: 0.95em;
        }
      }

      .save-button {
        margin-bottom: 12px;
      }
    }

    .action-form {
      margin-bottom: 12px;

      .field-offered {
        background: $dashboard_background;
        width: 100%;
        height: 42px;
        border: 0;
        border-radius: 10px;
        padding: 0 14px;
        text-align: center;
        outline: none;
        color: $color_dark;
        font-weight: bold;
      }

      .text-info {
        margin-bottom: 8px;
        font-size: 0.875em;
        color: $color_gray;
      }
    }
  }
}
</style>
